import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AVAILABLE_LANGS } from 'app-config';

import i18n from 'segments/i18n';

import {
  getUserTimezone,
  setUserTimezoneToLocalstorage,
} from '../utils/localStorage';

import type { AccountData, UserSettings } from './types';
import { userApi } from './userApi';


export interface UserState {
  account: AccountData;
  userSettings: UserSettings;
}

const initialState: UserState = {
  account: {
    id: '',
    email: '',
    role: null,
    name: '',
  },
  userSettings: {
    timezone: getUserTimezone(),
    lang: '',
    colorScheme: '',
    balanceType: '',
  }
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setTimzone(state, action: PayloadAction<{ timezone: number }>) {
      state.userSettings.timezone = action.payload.timezone;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        userApi.endpoints.account.matchFulfilled,
        (state: UserState, action: PayloadAction<AccountData>) => {
          state.account = action.payload;
        },
      )
      .addMatcher(
        userApi.endpoints.getUserSettings.matchFulfilled,
        (state: UserState, action: PayloadAction<UserSettings>) => {
          const { timezone, lang } = action.payload;
          state.userSettings = action.payload;

          if (timezone && Number(timezone) !== getUserTimezone()) {
            setUserTimezoneToLocalstorage(timezone);
          }

          if (AVAILABLE_LANGS.includes(lang) && lang !== i18n.resolvedLanguage) {
            i18n.changeLanguage(lang);
          }
        }
      );
  },
});

export const selectAccountData = (state: RootState) => state.user.account;
export const selectUserRole = (state: RootState) => state.user.account.role;
export const selectIsAdmin = (state: RootState) => state.user.account.role === 'admin';
export const selectUserTimzone = (state: RootState) => state.user.userSettings.timezone;

export const setUserTimezoneToState = userSlice.actions.setTimzone;
